(() => {
  const ENHANCED_NATIVE_CLASS = 'is-enhanced-native';

  const getSelectLabel = (select, wrapper) => {
    const selectedOption = select.options[select.selectedIndex] || select.options[0];
    const text = selectedOption ? selectedOption.textContent.trim() : '';
    const prefix = (wrapper.dataset.prefix || '').trim();
    return prefix ? `${prefix} ${text}`.trim() : text;
  };

  const setCustomSelectOpen = (wrapper, open) => {
    const button = wrapper.querySelector('[data-av-custom-select-button]') || wrapper.querySelector('.av-shop-custom-select__button');
    const menu = wrapper.querySelector('[data-av-custom-select-menu]') || wrapper.querySelector('.av-shop-custom-select__menu');

    wrapper.classList.toggle('is-open', open);

    if (button) {
      button.setAttribute('aria-expanded', String(open));
    }

    if (menu) {
      menu.hidden = !open;
    }
  };

  const closeCustomSelect = (wrapper) => {
    setCustomSelectOpen(wrapper, false);
  };

  const closeOtherCustomSelects = (currentWrapper) => {
    document.querySelectorAll('[data-av-custom-select].is-open').forEach((wrapper) => {
      if (wrapper !== currentWrapper) closeCustomSelect(wrapper);
    });
  };

  const updateCustomSelect = (wrapper) => {
    const select = wrapper.querySelector('select');
    const button = wrapper.querySelector('[data-av-custom-select-button]');
    const options = wrapper.querySelectorAll('[data-av-custom-select-option]');
    if (!select || !button) return;

    button.querySelector('[data-av-custom-select-text]').textContent = getSelectLabel(select, wrapper);
    options.forEach((option) => {
      const isSelected = option.dataset.value === select.value;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  };

  const enhanceCustomSelect = (wrapper) => {
    if (wrapper.dataset.avCustomSelectInitialized === 'true') return;

    const select = wrapper.querySelector('select');
    if (!select) return;

    wrapper.querySelectorAll('[data-av-custom-select-button], [data-av-custom-select-menu]').forEach((element) => element.remove());

    const button = document.createElement('button');
    const menu = document.createElement('div');
    const text = document.createElement('span');
    const caret = document.createElement('span');
    const menuId = `${select.id || `av-custom-select-${Date.now()}`}-menu`;

    button.type = 'button';
    button.className = 'av-shop-custom-select__button';
    button.dataset.avCustomSelectButton = '';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', menuId);

    text.className = 'av-shop-custom-select__text';
    text.dataset.avCustomSelectText = '';
    caret.className = 'av-shop-custom-select__caret';
    caret.setAttribute('aria-hidden', 'true');
    button.append(text, caret);

    menu.className = 'av-shop-custom-select__menu';
    menu.dataset.avCustomSelectMenu = '';
    menu.id = menuId;
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    Array.from(select.options).forEach((nativeOption) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'av-shop-custom-select__option';
      option.dataset.avCustomSelectOption = '';
      option.dataset.value = nativeOption.value;
      option.setAttribute('role', 'option');
      option.textContent = nativeOption.textContent;
      option.addEventListener('click', () => {
        select.value = nativeOption.value;
        updateCustomSelect(wrapper);
        select.dispatchEvent(new Event('change', { bubbles: true }));
        closeCustomSelect(wrapper);
        button.focus();
      });
      menu.appendChild(option);
    });

    button.addEventListener('click', (event) => {
      const willOpen = button.getAttribute('aria-expanded') !== 'true';
      closeOtherCustomSelects(wrapper);
      setCustomSelectOpen(wrapper, willOpen);

      if (!willOpen && event.detail > 0) {
        button.blur();
      }
    });

    wrapper.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeCustomSelect(wrapper);
        button.focus();
      }
    });

    select.addEventListener('change', () => updateCustomSelect(wrapper));
    select.classList.add(ENHANCED_NATIVE_CLASS);
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    wrapper.classList.add('is-enhanced');
    wrapper.append(button, menu);
    wrapper.dataset.avCustomSelectInitialized = 'true';
    updateCustomSelect(wrapper);
  };

  const enhanceCustomSelects = (root = document) => {
    root.querySelectorAll('[data-av-custom-select]').forEach(enhanceCustomSelect);
  };

  const initProductPillCarousels = (root = document) => {
    const carousels = root.querySelectorAll('[data-av-pill-carousel]');

    carousels.forEach((carousel) => {
      if (carousel.dataset.avPillCarouselReady === 'true') return;
      carousel.dataset.avPillCarouselReady = 'true';

      const track = carousel.querySelector('[data-av-pill-track]');
      const prev = carousel.querySelector('[data-av-pill-prev]');
      const next = carousel.querySelector('[data-av-pill-next]');

      if (!track || !prev || !next) return;

      const getStep = () => Math.max(140, Math.round(track.clientWidth * 0.72));
      const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

      const update = () => {
        const max = getMaxScroll();
        const hasOverflow = max > 4;
        const left = track.scrollLeft;

        prev.hidden = !hasOverflow || left <= 3;
        next.hidden = !hasOverflow || left >= max - 3;
      };

      prev.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        track.scrollBy({ left: -getStep(), behavior: 'smooth' });
      });

      next.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        track.scrollBy({ left: getStep(), behavior: 'smooth' });
      });

      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          track.scrollBy({ left: -getStep(), behavior: 'smooth' });
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          track.scrollBy({ left: getStep(), behavior: 'smooth' });
        }
      });

      let isDown = false;
      let startX = 0;
      let startScrollLeft = 0;

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        isDown = true;
        startX = event.clientX;
        startScrollLeft = track.scrollLeft;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
      });

      track.addEventListener('pointermove', (event) => {
        if (!isDown) return;
        event.preventDefault();
        const delta = event.clientX - startX;
        track.scrollLeft = startScrollLeft - delta;
      });

      const endDrag = (event) => {
        if (!isDown) return;
        isDown = false;
        track.classList.remove('is-dragging');
        try {
          track.releasePointerCapture?.(event.pointerId);
        } catch (error) {}
      };

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('lostpointercapture', endDrag);
      track.addEventListener('scroll', update, { passive: true });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(update);
        observer.observe(track);
      } else {
        window.addEventListener('resize', update);
      }

      window.setTimeout(update, 0);
      window.setTimeout(update, 250);
    });
  };

  const initProductCardLinks = (root = document) => {
    root.querySelectorAll('[data-av-product-card][data-av-product-url]').forEach((card) => {
      if (card.dataset.avProductCardLinkReady === 'true') return;
      card.dataset.avProductCardLinkReady = 'true';

      card.addEventListener('click', (event) => {
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof event.button === 'number' && event.button !== 0) return;

        const ignoredTarget = event.target.closest(
          'a, button, input, select, textarea, label, summary, [data-av-pill-carousel], [data-av-custom-select]'
        );

        if (ignoredTarget) return;

        const url = card.dataset.avProductUrl;
        if (!url) return;

        window.location.href = url;
      });
    });
  };


  const initMobileFilterControls = (toolbar, controls) => {
    const mobileRoot = toolbar.querySelector('[data-av-mobile-filters]');
    if (!mobileRoot || mobileRoot.dataset.avMobileFiltersInitialized === 'true') return;

    const { typeSelect, gameSelect, sortSelect, resetButton, updateGrid } = controls;
    if (!typeSelect || !gameSelect || !sortSelect || typeof updateGrid !== 'function') return;

    const filterToggle = mobileRoot.querySelector('[data-av-mobile-filter-toggle]');
    const sortToggle = mobileRoot.querySelector('[data-av-mobile-sort-toggle]');
    const filterPanel = mobileRoot.querySelector('[data-av-mobile-filter-panel]');
    const sortPanel = mobileRoot.querySelector('[data-av-mobile-sort-panel]');
    const overlay = mobileRoot.querySelector('[data-av-mobile-filter-overlay]');
    const clearButton = mobileRoot.querySelector('[data-av-mobile-filter-clear]');
    const typeOptionsRoot = mobileRoot.querySelector('[data-av-mobile-type-options]');
    const gameOptionsRoot = mobileRoot.querySelector('[data-av-mobile-game-options]');
    const sortOptionsRoot = mobileRoot.querySelector('[data-av-mobile-sort-options]');
    const chipsRoot = mobileRoot.querySelector('[data-av-mobile-filter-chips]');
    const filterCount = mobileRoot.querySelector('[data-av-mobile-filter-count]');
    const sortLabel = mobileRoot.querySelector('[data-av-mobile-sort-label]');

    if (!filterToggle || !sortToggle || !filterPanel || !sortPanel || !overlay || !typeOptionsRoot || !gameOptionsRoot || !sortOptionsRoot || !chipsRoot || !filterCount || !sortLabel) return;

    let draftTypeValue = typeSelect.value;
    let draftGameValue = gameSelect.value;

    const getSelectedOptionText = (select) => {
      const option = select.options[select.selectedIndex] || select.options[0];
      return option ? option.textContent.trim() : '';
    };

    const resetDraftsFromSelects = () => {
      draftTypeValue = typeSelect.value;
      draftGameValue = gameSelect.value;
    };

    const getDraftValueForSelect = (select) => {
      if (select === typeSelect) return draftTypeValue;
      if (select === gameSelect) return draftGameValue;
      return select.value;
    };

    const setDraftValueForSelect = (select, value) => {
      if (select === typeSelect) {
        draftTypeValue = value;
        return;
      }

      if (select === gameSelect) {
        draftGameValue = value;
      }
    };

    const setPanelOpen = (panelName) => {
      const filterOpen = panelName === 'filter';
      const sortOpen = panelName === 'sort';

      if (filterOpen) {
        resetDraftsFromSelects();
      }

      filterPanel.hidden = !filterOpen;
      sortPanel.hidden = !sortOpen;
      overlay.hidden = !filterOpen && !sortOpen;

      filterToggle.setAttribute('aria-expanded', String(filterOpen));
      sortToggle.setAttribute('aria-expanded', String(sortOpen));

      syncMobileControls();
    };

    const closePanels = () => setPanelOpen(null);
    const isFilterPanelOpen = () => !filterPanel.hidden;
    const isSortPanelOpen = () => !sortPanel.hidden;

    const setSelectValue = (select, value) => {
      if (select.value === value) return;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const buildOptions = (select, optionsRoot, onSelect) => {
      optionsRoot.replaceChildren();

      Array.from(select.options).forEach((nativeOption) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'av-shop-mobile-filter-option';
        button.dataset.value = nativeOption.value;
        button.textContent = nativeOption.textContent;
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => onSelect(nativeOption.value));
        optionsRoot.appendChild(button);
      });
    };

    const syncOptionGroup = (select, optionsRoot, valueOverride = select.value) => {
      optionsRoot.querySelectorAll('[data-value]').forEach((button) => {
        const isSelected = button.dataset.value === valueOverride;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    };

    const createChip = (select, label, ariaLabel) => {
      const chip = document.createElement('span');
      const removeButton = document.createElement('button');

      chip.className = 'av-shop-mobile-filter-chip';
      chip.textContent = label;
      removeButton.type = 'button';
      removeButton.textContent = '×';
      removeButton.setAttribute('aria-label', ariaLabel);
      removeButton.addEventListener('click', () => {
        setSelectValue(select, '');
        syncMobileControls();
      });
      chip.appendChild(removeButton);

      return chip;
    };

    const syncMobileControls = () => {
      const activeFilters = [typeSelect, gameSelect].filter((select) => select.value).length;

      syncOptionGroup(typeSelect, typeOptionsRoot, getDraftValueForSelect(typeSelect));
      syncOptionGroup(gameSelect, gameOptionsRoot, getDraftValueForSelect(gameSelect));
      syncOptionGroup(sortSelect, sortOptionsRoot, sortSelect.value);

      chipsRoot.replaceChildren();
      if (typeSelect.value) {
        chipsRoot.appendChild(createChip(typeSelect, getSelectedOptionText(typeSelect), 'Remove product type filter'));
      }
      if (gameSelect.value) {
        chipsRoot.appendChild(createChip(gameSelect, getSelectedOptionText(gameSelect), 'Remove compatibility filter'));
      }
      chipsRoot.hidden = activeFilters === 0;

      filterCount.textContent = String(activeFilters);
      filterCount.hidden = activeFilters === 0;
      sortLabel.textContent = `Sort: ${getSelectedOptionText(sortSelect)}`;
    };

    buildOptions(typeSelect, typeOptionsRoot, (value) => {
      setDraftValueForSelect(typeSelect, value);
      syncMobileControls();
    });
    buildOptions(gameSelect, gameOptionsRoot, (value) => {
      setDraftValueForSelect(gameSelect, value);
      syncMobileControls();
    });
    buildOptions(sortSelect, sortOptionsRoot, (value) => {
      setSelectValue(sortSelect, value);
      syncMobileControls();
      closePanels();
    });

    const applyDraftFilters = () => {
      const nextTypeValue = draftTypeValue;
      const nextGameValue = draftGameValue;

      const changed =
        typeSelect.value !== nextTypeValue ||
        gameSelect.value !== nextGameValue;

      typeSelect.value = nextTypeValue;
      gameSelect.value = nextGameValue;

      resetDraftsFromSelects();

      if (changed) {
        updateGrid();
      }

      syncMobileControls();
      closePanels();
    };

    const cancelDraftFilters = () => {
      resetDraftsFromSelects();
      syncMobileControls();
      closePanels();
    };

    filterToggle.addEventListener('click', () => {
      if (isFilterPanelOpen()) {
        cancelDraftFilters();
      } else {
        setPanelOpen('filter');
      }
    });

    sortToggle.addEventListener('click', () => {
      if (isFilterPanelOpen()) {
        cancelDraftFilters();
        setPanelOpen('sort');
        return;
      }

      setPanelOpen(isSortPanelOpen() ? null : 'sort');
    });

    overlay.addEventListener('click', () => {
      if (isFilterPanelOpen()) {
        cancelDraftFilters();
      } else {
        closePanels();
      }
    });

    mobileRoot.querySelectorAll('[data-av-mobile-panel-close]').forEach((button) => {
      button.addEventListener('click', () => {
        if (isFilterPanelOpen()) {
          cancelDraftFilters();
        } else {
          closePanels();
        }
      });
    });

    const applyButton = mobileRoot.querySelector('[data-av-mobile-panel-done]');
    if (applyButton) {
      applyButton.addEventListener('click', applyDraftFilters);
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        const changed = typeSelect.value !== '' || gameSelect.value !== '';

        typeSelect.value = '';
        gameSelect.value = '';

        resetDraftsFromSelects();

        if (changed) {
          updateGrid();
        }

        syncMobileControls();
        closePanels();
      });
    }

    [typeSelect, gameSelect].forEach((select) => {
      select.addEventListener('change', () => {
        resetDraftsFromSelects();
        syncMobileControls();
      });
    });

    sortSelect.addEventListener('change', syncMobileControls);

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        window.setTimeout(() => {
          resetDraftsFromSelects();
          syncMobileControls();
        }, 0);
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      if (isFilterPanelOpen()) {
        cancelDraftFilters();
      } else if (isSortPanelOpen()) {
        closePanels();
      }
    });

    syncMobileControls();
    closePanels();
    mobileRoot.hidden = false;
    mobileRoot.dataset.avMobileFiltersInitialized = 'true';
  };

  const initToolbar = (toolbar) => {
    if (toolbar.dataset.avFiltersInitialized === 'true') return;

    enhanceCustomSelects(toolbar);

    const section = toolbar.closest('.av-shop-page');
    if (!section) return;

    const grid = section.querySelector('[data-av-shop-product-grid]');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[data-av-product-card]'));
    if (!cards.length) return;

    const typeSelect = toolbar.querySelector('[data-av-filter-type]');
    const gameSelect = toolbar.querySelector('[data-av-filter-game]');
    const sortSelect = toolbar.querySelector('[data-av-filter-sort]');
    const resetButton = toolbar.querySelector('[data-av-filter-reset]');
    const emptyState = section.querySelector('[data-av-filter-empty]');

    const getNumber = (value) => {
      const number = Number.parseInt(value || '0', 10);
      return Number.isNaN(number) ? 0 : number;
    };

    const sortCards = () => {
      const mode = sortSelect ? sortSelect.value : 'featured';
      const sortedCards = [...cards].sort((a, b) => {
        if (mode === 'price-asc') {
          return getNumber(a.dataset.avProductPrice) - getNumber(b.dataset.avProductPrice);
        }
        if (mode === 'price-desc') {
          return getNumber(b.dataset.avProductPrice) - getNumber(a.dataset.avProductPrice);
        }
        return getNumber(a.dataset.avProductIndex) - getNumber(b.dataset.avProductIndex);
      });

      sortedCards.forEach((card) => grid.appendChild(card));
    };

    const applyFilters = () => {
      const selectedType = typeSelect ? typeSelect.value : '';
      const selectedGame = gameSelect ? gameSelect.value : '';
      let visibleCount = 0;

      cards.forEach((card) => {
        const cardType = card.dataset.avProductType || '';
        const cardGames = (card.dataset.avProductGames || '').split(/\s+/).filter(Boolean);
        const typeMatches = !selectedType || cardType === selectedType;
        const gameMatches = !selectedGame || cardGames.includes(selectedGame);
        const isVisible = typeMatches && gameMatches;

        card.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      if (emptyState) {
        emptyState.hidden = visibleCount !== 0;
      }
    };

    const syncCustomSelects = () => {
      toolbar.querySelectorAll('[data-av-custom-select]').forEach(updateCustomSelect);
    };

    const updateGrid = () => {
      sortCards();
      applyFilters();
      syncCustomSelects();
    };

    [typeSelect, gameSelect, sortSelect].forEach((select) => {
      if (select) select.addEventListener('change', updateGrid);
    });

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        if (typeSelect) typeSelect.value = '';
        if (gameSelect) gameSelect.value = '';
        if (sortSelect) sortSelect.value = 'featured';
        updateGrid();
      });
    }

    initMobileFilterControls(toolbar, {
      typeSelect,
      gameSelect,
      sortSelect,
      resetButton,
      updateGrid,
    });

    toolbar.dataset.avFiltersInitialized = 'true';
    updateGrid();
  };

  const init = (root = document) => {
    enhanceCustomSelects(root);
    initProductPillCarousels(root);
    initProductCardLinks(root);
    root.querySelectorAll('.av-shop-page [data-av-shop-toolbar]').forEach(initToolbar);
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-av-custom-select]')) {
      closeOtherCustomSelects(null);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
